import React, { useState, useEffect } from 'react';
import { UserPlus } from 'lucide-react';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { restClient } from './RestClient';
import { InviteInfoResponse } from './Model';

interface AcceptInviteProps {
  token: string;
  onAcceptInvite: (token: string, username: string, isAdmin: boolean, isSuperUser: boolean) => void;
  onSwitchToLogin: () => void;
}

export function AcceptInvite({ token, onAcceptInvite, onSwitchToLogin }: AcceptInviteProps) {
  const [inviteInfo, setInviteInfo] = useState<InviteInfoResponse | null>(null);
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const addError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 8000);
  };

  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  // Fetch invite info on mount
  useEffect(() => {
    const fetchInviteInfo = async () => {
      try {
        const result = await restClient.getInviteInfo(token);
        if (result.IsSuccess) {
          setInviteInfo(result.Value);
        } else {
          switch (result.Error.code) {
            case 'INVALID_INVITE_TOKEN':
              setLoadError('This invitation link is invalid.');
              break;
            case 'EXPIRED_INVITE_TOKEN':
              setLoadError('This invitation has expired. Please ask the coach to send a new one.');
              break;
            case 'USED_INVITE_TOKEN':
              setLoadError('This invitation has already been used.');
              break;
            default:
              setLoadError(result.Error.description || 'Failed to load invitation details.');
          }
        }
      } catch {
        setLoadError('Unable to connect to server. Please check your connection.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInviteInfo();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nickname || nickname.length < 2) {
      addError('Nickname must be at least 2 characters long');
      return;
    }

    if (nickname.length > 30) {
      addError('Nickname must be at most 30 characters long');
      return;
    }

    const nicknamePattern = /^[A-Za-z0-9 -]+$/;
    if (!nicknamePattern.test(nickname)) {
      addError('Nickname can only contain letters, numbers, spaces, and hyphens');
      return;
    }

    if (!password || password.length < 6) {
      addError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      addError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await restClient.acceptInvite({ token, password, nickname });

      if (result.IsSuccess) {
        onAcceptInvite(result.Value.token, result.Value.username, result.Value.isAdmin, result.Value.isSuperUser);
      } else {
        switch (result.Error.code) {
          case 'INVALID_INVITE_TOKEN':
            addError('This invitation link is invalid.');
            break;
          case 'EXPIRED_INVITE_TOKEN':
            addError('This invitation has expired. Please ask the coach to send a new one.');
            break;
          case 'USED_INVITE_TOKEN':
            addError('This invitation has already been used.');
            break;
          default:
            addError(result.Error.description || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      addError('Unable to connect to server. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <ErrorNotification errors={errors} onDismiss={dismissError} />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo auth-logo-login">
            <UserPlus size={28} />
          </div>
          <h1 className="auth-title">Create Your Account</h1>
          {isLoading ? (
            <p className="auth-subtitle">Loading invitation details...</p>
          ) : loadError ? (
            <p className="auth-subtitle">{loadError}</p>
          ) : inviteInfo ? (
            <p className="auth-subtitle">
              <strong>{inviteInfo.invitedByName}</strong> has {inviteInfo.shareType === 'Team'
                ? <>shared their team selection <strong>'{inviteInfo.sharedName}'</strong> with you</>
                : <>invited you to view <strong>{inviteInfo.sharedName}</strong></>}
            </p>
          ) : null}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center p-6">
            <span className="spinner" />
          </div>
        )}

        {loadError && (
          <div className="mb-6">
            <div className="alert alert-error">{loadError}</div>
            <button
              onClick={onSwitchToLogin}
              className="btn btn-lg btn-full btn-primary mt-4"
            >
              Go to sign in
            </button>
          </div>
        )}

        {!isLoading && !loadError && inviteInfo && (
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="form-group-lg">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={inviteInfo.email}
                readOnly
                className="form-control form-control-lg"
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Nickname</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Choose a nickname (2-30 characters)"
                disabled={isSubmitting}
                className="form-control form-control-lg"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password (min 6 characters)"
                disabled={isSubmitting}
                className="form-control form-control-lg"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={isSubmitting}
                className="form-control form-control-lg"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn btn-lg btn-full ${isSubmitting ? 'btn-secondary' : 'btn-primary'}`}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  Creating account...
                </>
              ) : (
                inviteInfo?.shareType === 'Team' ? 'Create Account & View Team' : 'Create Account & View Profile'
              )}
            </button>
          </form>
        )}

        {!isLoading && !loadError && (
          <div className="auth-footer">
            <p className="auth-footer-text">Already have an account?</p>
            <button
              onClick={onSwitchToLogin}
              disabled={isSubmitting}
              className="btn-link"
              style={{ opacity: isSubmitting ? 0.5 : 1 }}
            >
              Sign in instead
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
